import {
  type FC,
  type KeyboardEvent,
  type UIEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import { Icon as UIIcon } from "@alpacahq/ui/old";

import { Icon } from "~/v2/components/AssetIcon/AssetIcon";
import { cn } from "~/v2/utils/cn";
import { AssetQuote } from "./AssetQuote";

interface TabData {
  id: string;
  label: string;
  count: number;
}

interface EnrichedAsset {
  id: string;
  symbol: string;
  name: string;
  status: string;
  tradable: boolean;
  price?: number;
  dailyChange?: number;
  dailyChangePercent?: number;
  isQuoteLoading?: boolean;
  showOvernightHaltedIcon?: boolean;
  showNoOvernightSupportIcon?: boolean;
}

interface AssetSearchPresenterProps {
  query: string;
  onQueryChange: (val: string) => void;
  onFocus: () => void;
  onClose: () => void;
  isOpen: boolean;

  // Data
  assets: EnrichedAsset[];

  // Tabs
  tabs: readonly TabData[];
  activeTab: string;
  onTabChange: (id: string) => void;

  // State
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSelect: (asset: EnrichedAsset) => void;
  placeholder: string;
  autofocus?: boolean;

  // Styling
  showFilters?: boolean;
}

export const AssetSearchPresenter: FC<AssetSearchPresenterProps> = ({
  query,
  onQueryChange,
  onFocus,
  onClose,
  isOpen,
  assets,
  tabs,
  activeTab,
  onTabChange,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  onSelect,
  placeholder,
  autofocus,
  showFilters = true,
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isOpen and query are the triggers: the highlighted result is reset when the dropdown opens or the search text changes. The body only writes state, which is why the rule sees them as unnecessary.
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [isOpen, query]);

  useEffect(() => {
    if (autofocus) {
      inputRef.current?.focus();
    }
  }, [autofocus]);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop - clientHeight < 50 &&
      hasMore &&
      !isLoadingMore
    ) {
      onLoadMore();
    }
  };

  const scrollToHighlighted = (index: number) => {
    if (listRef.current) {
      const items = listRef.current.querySelectorAll("[data-asset-item]");
      if (items[index]) {
        items[index].scrollIntoView({ block: "nearest" });
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && isOpen) {
      e.preventDefault();
      onClose();
      return;
    }

    if (!isOpen || isLoading || assets.length === 0) {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev < assets.length - 1 ? prev + 1 : prev;
          scrollToHighlighted(next);
          return next;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : 0;
          scrollToHighlighted(next);
          return next;
        });
        break;
      case "Enter": {
        e.preventDefault();
        // If no item highlighted, select the first one; otherwise select highlighted
        const selectedIndex = highlightedIndex === -1 ? 0 : highlightedIndex;
        onSelect(assets[selectedIndex]);
        break;
      }
    }
  };

  return (
    <div className="relative">
      {/* --- Search Input --- */}
      <div className="group relative">
        <div className="pointer-events-none absolute inset-y-0 left-2 flex items-center">
          <UIIcon className="h-5 w-5 text-[#7A7A7A]" name="MagnifyingGlass" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            "w-full border border-[#9C9C9C] bg-white p-2 pl-9 font-medium text-[#7A7A7A] text-sm focus:outline-none focus:ring-0",
            showFilters ? "rounded-lg" : "rounded-[4px]",
          )}
        />
      </div>

      {/* --- Dropdown --- */}
      {isOpen && (
        <div
          className={cn(
            "absolute top-full left-0 z-50 mt-2 flex w-full flex-col overflow-hidden border border-[#9C9C9C] bg-[#FFF] shadow-lg",
            showFilters
              ? "max-h-[390px] rounded-lg"
              : "max-h-[346px] rounded-[4px]",
          )}
        >
          {/* --- Tabs Header --- */}
          {showFilters && (
            <div className="sticky top-0 z-10 bg-white px-2 pt-3 pb-0">
              <div className="flex items-center gap-4 border-[#D1D5DB] border-b">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      type="button"
                      key={tab.id}
                      onClick={() => onTabChange(tab.id)}
                      className={cn(
                        "relative flex items-center gap-2 pb-3 font-medium text-xs outline-none transition-colors",
                        isActive
                          ? "text-indigo-600"
                          : "text-gray-500 hover:text-gray-700",
                      )}
                    >
                      <span className="mt-0.5">{tab.label}</span>
                      {tab.count >= 0 && (
                        <span
                          className={cn(
                            "rounded-full px-1.5 py-0.5 font-mono font-semibold text-xs",
                            isActive
                              ? "bg-indigo-50 text-[#4F46E5]"
                              : "bg-gray-100 text-[#565656]",
                          )}
                        >
                          {tab.count}
                        </span>
                      )}
                      {isActive && (
                        <div className="absolute bottom-0 left-0 h-0.5 w-full rounded-t-full bg-[#4F46E5]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* --- Scrollable List --- */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto scroll-smooth p-1"
            onScroll={handleScroll}
          >
            {/* 1. Initial Loading State (Skeleton) */}
            {isLoading && assets.length === 0 ? (
              <div className={cn("flex flex-col gap-1", showFilters && "mt-2")}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <AssetQuote
                    key={i}
                    isLoading={true}
                    isCompact={!showFilters}
                  />
                ))}
              </div>
            ) : (
              <div className={cn("flex flex-col gap-1", showFilters && "mt-2")}>
                {/* 2. Actual Asset List */}
                {assets.map((asset, index) => (
                  <div key={asset.id} data-asset-item>
                    <AssetQuote
                      as="button"
                      onClick={() => onSelect(asset)}
                      symbol={asset.symbol}
                      companyName={asset.name}
                      price={asset.price}
                      dailyChange={asset.dailyChange}
                      dailyChangePercent={asset.dailyChangePercent}
                      icon={<Icon name={asset.symbol} />}
                      showNotTradableIcon={!asset.tradable}
                      showOvernightHaltedIcon={asset.showOvernightHaltedIcon}
                      showNoOvernightSupportIcon={
                        asset.showNoOvernightSupportIcon
                      }
                      isLoading={isLoading}
                      isQuoteLoading={asset.isQuoteLoading}
                      isHighlighted={highlightedIndex === index}
                      isCompact={!showFilters}
                    />
                  </div>
                ))}

                {/* 3. Infinite Scroll Loading (Skeleton at bottom) */}
                {isLoadingMore && (
                  <div className="flex flex-col gap-1">
                    {[1, 2].map((i) => (
                      <AssetQuote key={`more-${i}`} isLoading={true} />
                    ))}
                  </div>
                )}

                {/* 4. Empty States */}
                {!isLoading && assets.length === 0 && (
                  <div className="flex h-[340px] flex-col items-center justify-center gap-y-4 text-center text-gray-400 text-sm">
                    <Icon name="AR" size="xl" />
                    <h2 className="mb-0 text-[#3B3B3B] text-lg">
                      Try another search
                    </h2>
                    <p className="max-w-[413px] text-[#565656] text-sm">
                      No assets matched your entry. Check for typos or try a
                      different symbol.
                    </p>
                  </div>
                )}

                {!hasMore && assets.length > 0 && (
                  <div className="p-4 text-center text-gray-300 text-xs">
                    End of results
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
